import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { StatusPill } from '../components/StatusPill.js'
import { Icon } from '../components/Icon.js'
import { LinkButton } from '../components/Button.js'

export const Route = createFileRoute('/_app/students/')({
  component: StudentsPage,
})

interface StudentRow {
  id: string
  fullName: string
  classes: { id: string; name: string }[]
  examBoard: string | null
  schoolName: string | null
  isActive: boolean
  deletedAt: string | null
  createdAt: string
}

type StatusFilter = 'all' | 'active' | 'inactive'

function StudentsPage() {
  const qc = useQueryClient()
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active')
  const [classFilter, setClassFilter] = useState<string>('all')

  const students = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      const res = await api<StudentRow[]>('/api/v1/students')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // Status toggle: flip is_active in place (PATCH) rather than soft-deleting,
  // so a deactivated student still shows under the Inactive filter and can be
  // reactivated. Soft delete (DELETE) is reserved for true removal.
  const setActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const res = await api<{ student: StudentRow; classes: { id: string; name: string }[] }>(
        `/api/v1/students/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ isActive }),
        },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    // Flip the row optimistically and reconcile with the server response; no
    // eager refetch (avoids any read-after-write race and a redundant fetch).
    onMutate: async ({ id, isActive }) => {
      await qc.cancelQueries({ queryKey: ['students'] })
      const previous = qc.getQueryData<StudentRow[]>(['students'])
      qc.setQueryData<StudentRow[]>(['students'], (rows) =>
        (rows ?? []).map((s) => (s.id === id ? { ...s, isActive } : s)),
      )
      return { previous }
    },
    // Merge (not replace): the PATCH returns student + classes, so we spread
    // to avoid blanking any fields not included in the patch response.
    onSuccess: (updated, { id }) => {
      qc.setQueryData<StudentRow[]>(['students'], (rows) =>
        (rows ?? []).map((s) =>
          s.id === id ? { ...s, ...updated.student, classes: updated.classes } : s,
        ),
      )
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['students'], ctx.previous)
    },
  })

  // Build class filter options by flattening each student's classes[], deduped by id.
  const classOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const s of students.data ?? []) {
      for (const c of s.classes) {
        if (!seen.has(c.id)) seen.set(c.id, c.name)
      }
    }
    return Array.from(seen.entries())
  }, [students.data])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (students.data ?? []).filter((s) => {
      if (statusFilter === 'active' && !s.isActive) return false
      if (statusFilter === 'inactive' && s.isActive) return false
      if (classFilter !== 'all' && !s.classes.some((c) => c.id === classFilter)) return false
      if (q) {
        const classNames = s.classes.map((c) => c.name).join(' ')
        const hay = `${s.fullName} ${s.schoolName ?? ''} ${classNames}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [students.data, query, statusFilter, classFilter])

  const columns: Column<StudentRow>[] = [
    {
      key: 'name',
      header: 'Student',
      render: (s) => (
        <div>
          <Link
            to="/students/$id"
            params={{ id: s.id }}
            className="font-medium text-fg hover:text-[var(--color-primary)]"
          >
            {s.fullName}
          </Link>
          {s.schoolName ? (
            <p className="text-xs text-fg-subtle">{s.schoolName}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'class',
      header: 'Class',
      render: (s) =>
        s.classes.length === 0 ? (
          <span className="text-fg-subtle italic">Unassigned</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {s.classes.map((c) => (
              <span
                key={c.id}
                className="rounded-full border border-border bg-surface-muted px-2 py-0.5 text-xs text-fg-muted"
              >
                {c.name}
              </span>
            ))}
          </div>
        ),
    },
    {
      key: 'board',
      header: 'Exam board',
      render: (s) =>
        s.examBoard ? (
          <span className="uppercase text-xs font-medium text-fg-muted">
            {s.examBoard.replace('_', ' ')}
          </span>
        ) : (
          <span className="text-fg-subtle">-</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <StatusPill tone={s.isActive ? 'active' : 'cancelled'}>
          {s.isActive ? 'Active' : 'Inactive'}
        </StatusPill>
      ),
    },
    {
      key: 'created',
      header: 'Joined',
      align: 'right',
      render: (s) => (
        <span className="text-sm text-fg-muted">
          {new Date(s.createdAt).toLocaleDateString('en-IN')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (s) =>
        s.isActive ? (
          <button
            onClick={() => {
              if (confirm(`Mark ${s.fullName} inactive? They can be reactivated later.`)) {
                setActive.mutate({ id: s.id, isActive: false })
              }
            }}
            className="text-xs text-fg-muted hover:text-danger"
          >
            Deactivate
          </button>
        ) : (
          <button
            onClick={() => setActive.mutate({ id: s.id, isActive: true })}
            className="text-xs text-fg-muted hover:text-[var(--color-primary)]"
          >
            Reactivate
          </button>
        ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Students"
        description={
          students.data
            ? `${filtered.length} of ${students.data.length} students`
            : 'Active students in your academy.'
        }
        actions={
          <LinkButton to="/students/new">
            <Icon name="plus" size={14} />
            Add student
          </LinkButton>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <label className="relative flex-1 min-w-[200px] max-w-sm">
          <span className="sr-only">Search students</span>
          <Icon
            name="search"
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-faint"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, school, class..."
            className="w-full rounded-md border border-border bg-surface pl-9 pr-3 py-2 text-sm text-fg placeholder-fg-faint shadow-sm focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </label>

        <select
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
          className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg shadow-sm focus:outline-none focus:border-[var(--color-primary)]"
        >
          <option value="all">All classes</option>
          {classOptions.map(([cId, name]) => (
            <option key={cId} value={cId}>
              {name}
            </option>
          ))}
        </select>

        <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
          {(['active', 'all', 'inactive'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-surface-subtle text-fg font-medium'
                  : 'bg-surface text-fg-muted hover:bg-surface-muted'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(s) => s.id}
        loading={students.isLoading}
        density="compact"
        stickyHeader
        empty={
          students.data && students.data.length === 0
            ? 'No students yet. Add your first student to onboard a parent.'
            : 'No students match these filters.'
        }
      />
    </div>
  )
}
